import React from 'react';
import ReactDOM from 'react-dom';
import thunk from 'redux-thunk';

import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { createStore, applyMiddleware, compose, combineReducers } from 'redux';
import { Provider } from 'react-redux';

import SendFilesSelector from './components/send-files-selector/send-files-selector';
import NCFileManager from './components/ncFileManager/ncFileManager';
import FormEditorComponent from './components/form-editor/';
import ReverseMappingComponent from './components/reverse-mapping';
import CustomForm from './components/custom-form';
import CalendarSchedulerComponent from './components/calendar-scheduler/calendar-scheduler';
import SendPolicyPackage from './components/send-files-selector/send-policy-package';

import PipelinesComponent from './components/pipelines/pipelines'; //used if old pages use these components

import EndorsementsOverview from './components/pipelines/pages/endorsements-overview';
import OpportunitiesOverview from './components/pipelines/pages/opportunities-overview/';
import ServiceRequestsOverview from './components/pipelines/pages/service-requests-overview';
import TasksOverview from './components/pipelines/pages/tasks-overview';

import CoverPageComponent from './components/cover-page';

import permissionReducer from './shared/store/reducers/permission';
import sharedReducer from './shared/store/reducers/shared';

import signatureReducer from './components/signature/store/reducers/signature';

import filesReducer from './components/ncFileManager/store/reducers/files';
import sendFilesReducer from './components/ncFileManager/store/reducers/send-files';
import phoneBookReducer from './components/ncFileManager/store/reducers/phone-book';

import tasksPipelinesReducer from './components/pipelines/store/reducers/tasks-pipelines';
import serviceRequestsPipelinesReducer from './components/pipelines/store/reducers/service-requests-pipelines';
import opportunitiesPipelinesReducer from './components/pipelines/store/reducers/opportunities-pipelines';
import endorsementsPipelinesReducer from './components/pipelines/store/reducers/endorsements-pipelines';

import formEditorReducer from './components/form-editor/store/reducers';

import reverseMappingReducer from './components/reverse-mapping/store/reducers/reverse-mapping-reducer';

import customFormReducer from './components/custom-form/store/reducers';

import smsesReducer from './components/smses/store/reducers/smses';
import smsChatReducer from './components/smses/store/reducers/sms-chat';

import webSocketReducer from './shared/store/reducers/web-socket';

import calendarSchedulerReducer from './components/calendar-scheduler/store/reducers/calendar-events-reducer';

import rabbitSignReducer from './shared/store/reducers/rabbit-sign-reducers';

import originalPdfFormsReducer from './components/original-pdf-forms/store/reducers/original-pdf-forms';
import CoverPageReducer from './components/cover-page/store/reducers';

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

const rootReducer = combineReducers({
  permissions: permissionReducer,
  shared: sharedReducer,

  signature: signatureReducer,

  sendFiles: sendFilesReducer,

  files: filesReducer,

  phoneBook: phoneBookReducer,

  tasksPipelines: tasksPipelinesReducer,
  serviceRequestsPipelines: serviceRequestsPipelinesReducer,
  opportunitiesPipelines: opportunitiesPipelinesReducer,
  endorsementsPipelines: endorsementsPipelinesReducer,

  formEditor: formEditorReducer,

  reverseMapping: reverseMappingReducer,

  customForm: customFormReducer,

  smses: smsesReducer,
  smsChat: smsChatReducer,

  webSocket: webSocketReducer,

  calendarScheduler: calendarSchedulerReducer,
  rabbitSign: rabbitSignReducer,

  originalPdfForms: originalPdfFormsReducer,
  coverPage: CoverPageReducer,
});

const store = createStore(rootReducer, composeEnhancers(applyMiddleware(thunk)));

const lazyLoadCalendarScheduler = (event) => {
  const calendarSchedulerRoot = document.getElementById('calendar-scheduler-root-lazy');

  if (calendarSchedulerRoot) {
    const app = (
      <Provider store={store}>
        <CalendarSchedulerComponent rootContainer={() => calendarSchedulerRoot} />
      </Provider>
    );

    ReactDOM.render(app, calendarSchedulerRoot);
  }
};

document.addEventListener('NCM_LAZY_LOAD_REACT_CALENDAR_SCHEDULER', lazyLoadCalendarScheduler, false);

const lazyLoadFiles = () => {
  const filesRoot = document.getElementById('files-root-lazy');

  if (filesRoot) {
    const app = (
      <Provider store={store}>
        <NCFileManager rootContainer={() => filesRoot} />
      </Provider>
    );

    ReactDOM.render(app, filesRoot);
  }
};

document.addEventListener('NCM_LAZY_LOAD_REACT_FILES', lazyLoadFiles, false);

const lazyLoadSendFilesSelector = () => {
  const sendFilesSelectorRoot = document.getElementById('send-files-selector-root-lazy');

  if (sendFilesSelectorRoot) {
    const app = (
      <DndProvider backend={HTML5Backend}>
        <Provider store={store}>
          <SendFilesSelector rootContainer={() => sendFilesSelectorRoot} />
        </Provider>
      </DndProvider>
    );

    ReactDOM.render(app, sendFilesSelectorRoot);
  }
};

document.addEventListener('NCM_LAZY_LOAD_REACT_SEND_FILES_SELECTOR', lazyLoadSendFilesSelector, false);

const lazyLoadFormEditor = (event) => {
  const formEditorRoot = document.getElementById('form-editor-root-lazy');

  if (formEditorRoot) {
    const formEditorConfig = event.detail;

    const app = (
      <Provider store={store}>
        <FormEditorComponent
          rootContainer={() => formEditorRoot}
          formId={formEditorConfig.formId}
          agentId={formEditorConfig.agentId}
          policyId={formEditorConfig.policyId}
          contactId={formEditorConfig.contactId}
          quoteRequestId={formEditorConfig.quoteRequestId}
          redirectUrl={formEditorConfig.redirectUrl}
        />
      </Provider>
    );

    ReactDOM.render(app, formEditorRoot);
  }
};

document.addEventListener('NCM_LAZY_LOAD_REACT_FORM_EDITOR', lazyLoadFormEditor, false);

const lazyLoadReverseMapping = (event) => {
  const reverseMappingRoot = document.getElementById('reverse-mapping-root-lazy');

  if (reverseMappingRoot) {
    const reverseMappingConfig = event.detail;

    const app = (
      <Provider store={store}>
        <ReverseMappingComponent
          rootContainer={() => reverseMappingRoot}
          userId={reverseMappingConfig.userId}
          agencyId={reverseMappingConfig.agencyId}
          formId={reverseMappingConfig.formId}
          reverseMappingType={reverseMappingConfig.reverseMappingType}
          redirectUrl={reverseMappingConfig.redirectUrl}
        />
      </Provider>
    );

    ReactDOM.render(app, reverseMappingRoot);
  }
};

document.addEventListener('NCM_LAZY_LOAD_REACT_REVERSE_MAPPING', lazyLoadReverseMapping, false);

// #region Pipelines

const lazyLoadPipelines = (event) => {
  const pipelinesRoot = document.getElementById('pipelines-root-lazy');

  if (pipelinesRoot) {
    const pipelinesConfig = event.detail;

    const app = (
      <Provider store={store}>
        <PipelinesComponent rootContainer={() => pipelinesRoot} />
      </Provider>
    );

    ReactDOM.render(app, pipelinesRoot);
  }
};

document.addEventListener('NCM_LAZY_LOAD_REACT_PIPELINES', lazyLoadPipelines, false);

const lazyLoadOpportunitiesOverview = (event) => {
  const pipelinesRoot = document.getElementById('opportunities-overview-root-lazy');

  if (pipelinesRoot) {
    const pipelinesConfig = event.detail;

    const app = (
      <Provider store={store}>
        <OpportunitiesOverview rootContainer={() => pipelinesRoot} opportunitiesOverview={true} />
      </Provider>
    );

    ReactDOM.render(app, pipelinesRoot);
  }
};

document.addEventListener('NCM_LAZY_LOAD_REACT_OPPORTUNITIES_OVERVIEW', lazyLoadOpportunitiesOverview, false);

const lazyLoadRenewalOverview = (event) => {
  const pipelinesRoot = document.getElementById('renewals-overview-root-lazy');

  if (pipelinesRoot) {
    const pipelinesConfig = event.detail;

    const app = (
      <Provider store={store}>
        <OpportunitiesOverview rootContainer={() => pipelinesRoot} renewalOverview={true} />
      </Provider>
    );

    ReactDOM.render(app, pipelinesRoot);
  }
};

document.addEventListener('NCM_LAZY_LOAD_REACT_RENEWAL_OVERVIEW', lazyLoadRenewalOverview, false);

const lazyLoadServiceRequestsOverview = (event) => {
  const pipelinesRoot = document.getElementById('service-requests-overview-root-lazy');

  if (pipelinesRoot) {
    const pipelinesConfig = event.detail;

    const app = (
      <Provider store={store}>
        <ServiceRequestsOverview rootContainer={() => pipelinesRoot} />
      </Provider>
    );

    ReactDOM.render(app, pipelinesRoot);
  }
};

document.addEventListener('NCM_LAZY_LOAD_REACT_SERVICE_REQUESTS_OVERVIEW', lazyLoadServiceRequestsOverview, false);

const lazyLoadEndorsementsOverview = (event) => {
  const pipelinesRoot = document.getElementById('endorsements-overview-root-lazy');

  if (pipelinesRoot) {
    const pipelinesConfig = event.detail;

    const app = (
      <Provider store={store}>
        <EndorsementsOverview rootContainer={() => pipelinesRoot} />
      </Provider>
    );

    ReactDOM.render(app, pipelinesRoot);
  }
};

document.addEventListener('NCM_LAZY_LOAD_REACT_ENDORSEMENTS_OVERVIEW', lazyLoadEndorsementsOverview, false);

const lazyLoadTasksOverview = (event) => {
  const pipelinesRoot = document.getElementById('tasks-overview-root-lazy');

  if (pipelinesRoot) {
    const pipelinesConfig = event.detail;

    const app = (
      <Provider store={store}>
        <TasksOverview rootContainer={() => pipelinesRoot} />
      </Provider>
    );

    ReactDOM.render(app, pipelinesRoot);
  }
};

document.addEventListener('NCM_LAZY_LOAD_REACT_TASKS_OVERVIEW', lazyLoadTasksOverview, false);

// #endregion Pipelines

const lazyLoadCustomForm = (event) => {
  const customFormRoot = document.getElementById('custom-form-root-lazy');

  if (customFormRoot) {
    const customFormConfig = event.detail;

    const app = (
      <Provider store={store}>
        <CustomForm rootContainer={() => customFormRoot} formId={customFormConfig.formId} redirectUrl={customFormConfig.redirectUrl} />
      </Provider>
    );

    ReactDOM.render(app, customFormRoot);
  }
};

document.addEventListener('NCM_LAZY_LOAD_REACT_CUSTOM_FORM', lazyLoadCustomForm, false);

const lazyLoadSendPolicyPackage = (event) => {
  const sendPolicyPackageRoot = document.getElementById('send-policy-package-root-lazy');

  if (sendPolicyPackageRoot) {
    const sendPolicyPackageConfig = event.detail;

    const app = (
      <DndProvider backend={HTML5Backend}>
        <Provider store={store}>
          <SendPolicyPackage
            rootContainer={() => sendPolicyPackageRoot}
            insuredId={sendPolicyPackageConfig.insuredId}
            agencyId={sendPolicyPackageConfig.agencyId}
            policyId={sendPolicyPackageConfig.policyId}
          />
        </Provider>
      </DndProvider>
    );

    ReactDOM.render(app, sendPolicyPackageRoot);
  }
};

document.addEventListener('NCM_LAZY_LOAD_REACT_SEND_POLICY_PACKAGE', lazyLoadSendPolicyPackage, false);

const lazyLoadCoverPage = (event) => {
  const coverPageRoot = document.getElementById('cover-page-root-lazy');

  if (coverPageRoot) {
    const app = (
      <Provider store={store}>
        <CoverPageComponent rootContainer={() => coverPageRoot} />
      </Provider>
    );

    ReactDOM.render(app, coverPageRoot);
  }
};

document.addEventListener('NCM_LAZY_LOAD_REACT_COVER_PAGE', lazyLoadCoverPage, false);
